import {createApi} from '@reduxjs/toolkit/query/react'
import type {
  BasicAffectedObjectsInterface,
  SignalsAffectedObjectsResponseInterface,
} from '@interfaces/affectedObjects'
import type {SubscriptionTestResponseInterface} from '@interfaces/notificationSubscriptions'
import type {
  BaseFetchResultInterface,
  BaseFetchResultsInterface,
  BasicObjectWithDescriptionInterface,
} from '@interfaces/base'
import type {BatchInterface} from '@interfaces/batch'
import type {PaginatedResponse} from '@interfaces/common/paginatedResponse'
import type {DashboardInterface} from '@interfaces/dashboard'
import type {EquipmentInterface} from '@interfaces/equipment'
import type {EquipmentTraceInterface} from '@interfaces/equipmentTrace'
import type {SmartFilterInterface} from '@interfaces/smartFilter'
import type {ExternalBookmarkInterface} from '@interfaces/externalBookmarks'
import type {
  LineInterface,
  LineWithEquipmentInterface,
  LineWithNumberOfSignals,
} from '@interfaces/line'
import type {NotificationInterface} from '@interfaces/notification'
import type {ProgramInterface} from '@interfaces/program'
import type {
  DerivedSignalRollupPayloadInterface,
  RollupAggregatorTermInterface,
  RollupAggregatorTermPayload,
  SignalRollupInterface,
  SignalRollupPayloadInterface,
} from '@interfaces/signalRollup'
import type {SubscriptionInterface, SubscriptionUsageInterface} from '@interfaces/subscription'
import type {TenantLogbookEntriesResult} from '@interfaces/tenantLogbookEntries'
import type {TriggerInterface} from '@interfaces/trigger'
import type {HorizontalLineInterface} from '@interfaces/view'
import {
  DEFAULT_SELECTION_LIST_LIMIT,
  DEFAULT_EQUIPMENT_PAGE_SIZE,
  DEFAULT_OFFSET,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SIGNAL_ROLLUPS_PAGE_SIZE,
} from '@shared/constants/preferences/pagination'
import type {
  SignalValueTrackerInterface,
  SignalValueTrackerTopValuesResponseInterface,
} from '@interfaces/signalValueTrackerInterface.ts'
import {createMontBlancBaseQuery} from '@apis/helpers.ts'

// const fullUrl = process.env.NODE_ENV === 'development' ? 'http://local-montblanc-backend.ai:8000' : ''

// const fullUrl = process.env.REACT_APP_API_BASE_URL === '' ? '' : process.env.REACT_APP_API_BASE_URL

/**
 * Dev-only tripwire for the `with_value` endpoints: without an id filter (and no
 * equipment/batch scope) the backend evaluates EVERY rollup in the tenant — a
 * silently expensive request (thousands of TSDB evaluations). Callers guard today;
 * this makes the next unguarded call loud instead of slow.
 */
const warnOnUnfilteredRollupEvaluation = (args: {
  signalRollupId?: string
  signalRollupIds?: string[]
  equipmentId?: string
  batchId?: string
}): void => {
  if (!import.meta.env.DEV) return
  const hasIdFilter = Boolean(args.signalRollupIds?.length || args.signalRollupId)
  if (!hasIdFilter && !args.equipmentId && !args.batchId) {
    console.warn(
      'signalrollups/with_value/ called without id, equipment, or batch filter — this evaluates every rollup in the tenant.',
    )
  }
}

// Define a service using a base URL and expected endpoints
export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: createMontBlancBaseQuery('core'),
  tagTypes: [
    'DisplayScreens',
    'LogbookCategory',
    'MasterRecipe',
    'ReportTemplates',
    'Reports',
    'ReportCapabilities',
    'ReportLetterheads',
    // Per-record, so saving one template refreshes the timeline open beside it
    // rather than every timeline in the tab (AB#5835).
    'ReportTemplateVersions',
    'ReportLetterheadVersions',
    'ProductionSite',
    'ProductionArea',
    'EquipmentGroup',
    'Floor',
    'CockpitViolations',
    'Line',
    'Equipment',
    'EquipmentLink',
    'EquipmentLinkInstance',
    'EquipmentLinkType',
    'MachineSubSystemType',
    'Signal',
    'SignalData',
    'SignalRollup',
    'SignalThreshold',
    'SignalValueDescription',
    'SignalLink',
    'Program',
    'Trigger',
    'SignalType',
    'User',
    'CustomDashboards',
    'CustomDashboard',
    'DashboardDraft',
    'DashboardVersions',
    'SmartFilter',
    'CurrentUser',
    'Incident',
    'IncidentTimeline',
    'ProgramClassification',
    'UsageHomeSummary',
    'ResourceGroup',
    'OrganizationSignalUsage',
    'BatchReportTemplates',
    'BatchReportTemplate',
    'BatchReports',
    'BatchReport',
    'Task',
    'AutomationRules',
    'AutomationRule',
    'AutomationRuns',
    'AutomationRun',
    'CallTemplates',
    'CallTemplate',
  ],
  endpoints: (builder) => ({
    getAllLogbookEntries: builder.query<
      TenantLogbookEntriesResult,
      {
        filters?: Record<string, string>
        page?: number
        pageSize?: number
        ordering?: string
      } | void
    >({
      query: ({filters = {}, page = 0, pageSize = DEFAULT_PAGE_SIZE, ordering} = {}) => {
        const queryParams = new URLSearchParams({
          ...filters,
          offset: (page * pageSize).toString(),
          limit: pageSize.toString(),
          ...(ordering ? {ordering} : {}),
        }).toString()
        return `logbook/${queryParams ? `?${queryParams}` : ''}`
      },
    }),
    getDashboards: builder.query<DashboardInterface[], void>({
      query: () => 'dashboards/',
      transformResponse: (response: BaseFetchResultsInterface<DashboardInterface>) =>
        response.results,
    }),
    getDashboardsById: builder.query<DashboardInterface, {id: string}>({
      query: ({id}) => `dashboards/${id}/`,
    }),
    getEquipmentTrace: builder.query<
      EquipmentTraceInterface,
      {equipmentId: string; traceDirection: string}
    >({
      query: ({equipmentId, traceDirection}) =>
        `machinesubsystems/${equipmentId}/trace/?trace_direction=${traceDirection}`,
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getExternalBookmarks: builder.query<
      PaginatedResponse<ExternalBookmarkInterface>,
      {limit: number; offset: number; ordering?: string}
    >({
      query: ({limit, offset, ordering}) => ({
        url: 'external_bookmarks/',
        params: {
          limit,
          offset,
          ordering,
        },
      }),
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getSignalRollups: builder.query<
      SignalRollupInterface[],
      {
        lineId?: string
        equipmentId?: string
        batchId?: string
        limit?: number
        offset?: number
        search?: string
      }
    >({
      query: ({lineId, equipmentId, batchId, limit, offset, search}) => ({
        url: 'signalrollups/',
        params: {
          // Scope by the rollup's home equipment (its own machine_sub_system
          // field), not its signal's location.
          machine_sub_system__machine: lineId,
          machine_sub_system: equipmentId,
          batch: batchId,
          limit,
          offset,
          search,
        },
      }),
      transformResponse: (response: BaseFetchResultsInterface<SignalRollupInterface>) =>
        response.results,
      extraOptions: {refetchOnMountOrArgChange: true},
      providesTags: ['SignalRollup'],
    }),
    getSignalRollupsByIds: builder.query<SignalRollupInterface[], {ids: string[]}>({
      query: ({ids}) =>
        `signalrollups/?id__in=${encodeURIComponent(ids.join(','))}&no_pagination=true`,
      transformResponse: (
        response: BaseFetchResultsInterface<SignalRollupInterface> | SignalRollupInterface[],
      ) => (Array.isArray(response) ? response : response.results),
      providesTags: ['SignalRollup'],
    }),
    getSignalRollupsWithValue: builder.query<
      SignalRollupInterface[],
      {
        signalRollupId?: string
        /** Batch form: evaluate several rollups over the same window in one call. */
        signalRollupIds?: string[]
        equipmentId?: string
        batchId?: string
        tStart?: string
        tEnd?: string
        limit?: number
        offset?: number
      }
    >({
      query: ({
        signalRollupId,
        signalRollupIds,
        equipmentId,
        batchId,
        limit,
        offset,
        tStart,
        tEnd,
      }) => {
        warnOnUnfilteredRollupEvaluation({signalRollupId, signalRollupIds, equipmentId, batchId})
        return {
          url: 'signalrollups/with_value/',
          params: {
            // The backend filterset only exposes id__in (bare `id` is silently ignored and
            // the endpoint would evaluate EVERY rollup in the tenant).
            id__in: signalRollupIds?.length ? signalRollupIds.join(',') : signalRollupId,
            machine_sub_system: equipmentId,
            batch: batchId,
            t_start: tStart,
            t_end: tEnd,
            limit,
            offset,
          },
        }
      },
      transformResponse: (response: BaseFetchResultsInterface<SignalRollupInterface>) =>
        response.results,
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getSignalRollupsWithValuePaginated: builder.query<
      BaseFetchResultsInterface<SignalRollupInterface>,
      {
        signalRollupId?: string
        equipmentId?: string
        batchId?: string
        tStart?: string
        tEnd?: string
        limit?: number
        offset?: number
      }
    >({
      query: ({signalRollupId, equipmentId, batchId, limit, offset, tStart, tEnd}) => {
        warnOnUnfilteredRollupEvaluation({signalRollupId, equipmentId, batchId})
        return {
          url: 'signalrollups/with_value/',
          params: {
            // The backend filterset only exposes id__in (bare `id` is silently ignored and
            // the endpoint would evaluate EVERY rollup in the tenant).
            id__in: signalRollupId,
            machine_sub_system: equipmentId,
            batch: batchId,
            t_start: tStart,
            t_end: tEnd,
            limit,
            offset,
          },
        }
      },
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getSignalRollupsPaginated: builder.query<
      BaseFetchResultsInterface<SignalRollupInterface>,
      {
        lineId?: string
        equipmentId?: string
        search?: string
        ordering?: string
        limit?: number
        offset?: number
        // Only rollups that can be a trace quantity (AB#6833).
        quantityOnly?: boolean
      }
    >({
      query: ({
        lineId,
        equipmentId,
        search,
        quantityOnly,
        ordering,
        limit = DEFAULT_SIGNAL_ROLLUPS_PAGE_SIZE,
        offset = DEFAULT_OFFSET,
      }) => ({
        url: 'signalrollups/',
        params: {
          // Scope by the rollup's own home equipment (machine_sub_system field on
          // the rollup — the source of truth), not its signal's location: the
          // signal may live anywhere in the tenant.
          machine_sub_system: equipmentId || undefined,
          machine_sub_system__machine: lineId || undefined,
          search: search || undefined,
          ordering: ordering || undefined,
          quantity_only: quantityOnly || undefined,
          limit,
          offset,
        },
      }),
      extraOptions: {refetchOnMountOrArgChange: true},
      providesTags: ['SignalRollup'],
    }),
    getThresholdsWithValue: builder.query<
      HorizontalLineInterface[],
      {
        signalId: string
        tStart: string
        tEnd: string
      }
    >({
      query: ({signalId, tStart, tEnd}) => ({
        url: 'thresholds/with_value/',
        params: {
          signal: signalId,
          t_start: tStart,
          t_end: tEnd,
        },
      }),
      transformResponse: (response: BaseFetchResultsInterface<HorizontalLineInterface>) =>
        response.results,
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getSignalsObjectsAffectedDestroy: builder.query<
      SignalsAffectedObjectsResponseInterface,
      {
        signalIds: string
        // The equipment the signals are moving to. With it, the backend lists
        // only the programs the move strips: programs on the target keep their
        // triggers, and a signal already there does not move (AB#6551).
        machineSubSystemId?: string
      }
    >({
      query: ({signalIds, machineSubSystemId}) => {
        const target = machineSubSystemId ? `&target_machine_sub_system=${machineSubSystemId}` : ''
        return `signals/affected_objects_by_machine_sub_system_reassignment/?id__in=${signalIds}${target}`
      },
    }),
    getLine: builder.query<
      LineInterface,
      {
        lineId: string
      }
    >({
      query: ({lineId}) => `machines/${lineId}/`,
      providesTags: ['Line'],
    }),
    getLines: builder.query<LineInterface[], {limit?: number; offset?: number; ordering?: string}>({
      query: ({limit, offset, ordering}) => {
        let queryString = `machines/?limit=${limit || 5000}&offset=${offset ?? 0}`
        if (ordering) {
          queryString += `&ordering=${ordering}`
        }
        return queryString
      },
      transformResponse: (response: BaseFetchResultsInterface<LineInterface>) => response.results,
      providesTags: ['Line'],
    }),
    getLinesWithCount: builder.query<
      {count: number; next: string | null; previous: string | null; results: LineInterface[]},
      {limit?: number; offset?: number; ordering?: string; search?: string}
    >({
      query: ({limit, offset, ordering, search}) => {
        let queryString = `machines/?limit=${limit || 5000}&offset=${offset ?? 0}`
        if (ordering) {
          queryString += `&ordering=${ordering}`
        }
        if (search) {
          queryString += `&search=${search}`
        }
        return queryString
      },
      providesTags: ['Line'],
    }),
    getLinesWithSearch: builder.query<
      LineInterface[],
      {search?: string; productionAreaId?: string; scoped?: boolean; lean?: boolean}
    >({
      // lean → the id+name action, skipping the full machine serializer's
      // per-row method fields (machine_sub_systems, number_of_signals, …).
      query: ({search, productionAreaId, scoped = true, lean}) => ({
        url: lean ? 'machines/lean/' : 'machines/',
        params: {
          search,
          production_area: productionAreaId,
          scoped: scoped,
        },
      }),
      transformResponse: (response: BaseFetchResultsInterface<LineInterface>) => response.results,
      providesTags: ['Line'],
    }),
    getLinesWithNumberOfSignals: builder.query<LineWithNumberOfSignals[], void>({
      query: () => 'machines/with_number_signals/',
      providesTags: ['Line'],
    }),
    getSubscriptions: builder.query<SubscriptionInterface[], void>({
      query: () => 'subscriptions/',
      transformResponse: (response: BaseFetchResultsInterface<SubscriptionInterface>) =>
        response.results,
    }),
    getLinesWithSubSystems: builder.query<LineWithEquipmentInterface[], {limit: number}>({
      query: ({limit}) => ({url: `machines/withmachinesubsystems/?limit=${limit}`}),
      extraOptions: {refetchOnMountOrArgChange: true},
      transformResponse: (response: BaseFetchResultsInterface<LineWithEquipmentInterface>) =>
        response.results,
      providesTags: ['Line'],
    }),
    getBuildings: builder.query<BasicObjectWithDescriptionInterface[], void>({
      query: () => 'buildings/',
      transformResponse: (
        response: BaseFetchResultsInterface<BasicObjectWithDescriptionInterface>,
      ) => response.results,
    }),
    // Machine types, paged and searched on the server. Both consumers are
    // dropdowns over a list with no guaranteed bound, so neither can assume it
    // fits in one response.
    getLineTypesPaginated: builder.query<
      BaseFetchResultsInterface<BasicObjectWithDescriptionInterface>,
      {search?: string; limit?: number; offset?: number}
    >({
      query: ({search, limit = 25, offset = 0}) => {
        const params = new URLSearchParams({limit: String(limit), offset: String(offset)})
        if (search) {
          params.set('search', search)
        }
        return `machinetypes/?${params.toString()}`
      },
    }),
    getEquipments: builder.query<
      EquipmentInterface[],
      {lineId: string; limit?: number; offset?: number; ordering?: string; search?: string}
    >({
      query: ({lineId, limit, offset, ordering, search}) => {
        let url = `machinesubsystems/?machine=${lineId}&limit=${limit || 5000}&offset=${offset || 0}`
        if (ordering) {
          url += `&ordering=${ordering}`
        }
        if (search) {
          url += `&search=${search}`
        }
        return {url}
      },
      transformResponse: (response: BaseFetchResultsInterface<EquipmentInterface>) =>
        response.results,
    }),
    getEquipmentsWithCount: builder.query<
      {count: number; next: string | null; previous: string | null; results: EquipmentInterface[]},
      {lineId: string; limit?: number; offset?: number; ordering?: string; search?: string}
    >({
      query: ({lineId, limit, offset, ordering, search}) => {
        let url = `machinesubsystems/?machine=${lineId}&limit=${limit || 5000}&offset=${offset || 0}`
        if (ordering) {
          url += `&ordering=${ordering}`
        }
        if (search) {
          url += `&search=${search}`
        }
        return {url}
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({
                type: 'Equipment' as const,
                id,
              })),
              {
                type: 'Equipment' as const,
                id: 'LIST',
              },
            ]
          : [
              {
                type: 'Equipment',
                id: 'LIST',
              },
            ],
    }),
    getSmartFilter: builder.query<BaseFetchResultsInterface<SmartFilterInterface>, void>({
      query: () => 'smart-filters/',
      extraOptions: {refetchOnMountOrArgChange: true},
    }),

    getEquipmentsWithSearch: builder.query<
      EquipmentInterface[],
      {
        search?: string
        lineId?: string
        productionAreaId?: string
        equipmentId?: string
        scoped?: boolean
      }
    >({
      query: ({search, lineId, productionAreaId, equipmentId, scoped = true}) => ({
        url: 'machinesubsystems/',
        params: {
          search,
          machine: lineId, // or 'line_id' if that's what your backend expects
          machine__production_area: productionAreaId, // or 'production_area_id' if that's what your backend expects
          id: equipmentId,
          scoped: scoped, // Add scoped parameter if needed
        },
      }),
      transformResponse: (response: BaseFetchResultsInterface<EquipmentInterface>) =>
        response.results,
    }),
    getEquipmentsWithSearchPaginated: builder.query<
      BaseFetchResultsInterface<EquipmentInterface>,
      {
        search?: string
        lineId?: string
        productionAreaId?: string
        scoped?: boolean
        limit?: number
        offset?: number
        ordering?: string
        includeIds?: string[]
        // Hit the lean id+name action — for pickers that don't need the full,
        // method-field-heavy equipment serializer.
        lean?: boolean
      }
    >({
      query: ({
        search,
        lineId,
        productionAreaId,
        scoped = true,
        limit = DEFAULT_EQUIPMENT_PAGE_SIZE,
        offset = DEFAULT_OFFSET,
        ordering,
        includeIds,
        lean,
      }) => ({
        url: lean ? 'machinesubsystems/lean/' : 'machinesubsystems/',
        params: {
          search,
          machine: lineId,
          machine__production_area: productionAreaId,
          scoped: scoped,
          limit,
          offset,
          ordering,
          id__in: includeIds?.length ? includeIds.join(',') : undefined,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({
                type: 'Equipment' as const,
                id,
              })),
              {
                type: 'Equipment' as const,
                id: 'LIST',
              },
            ]
          : [
              {
                type: 'Equipment',
                id: 'LIST',
              },
            ],
    }),
    getProgramDetectorTriggers: builder.query<
      BaseFetchResultsInterface<TriggerInterface>,
      {
        equipmentId?: string
        limit?: number
        offset?: number
        search?: string
        excludeIds?: string[]
        includeIds?: string[]
        ordering?: string
      }
    >({
      query: ({
        equipmentId,
        limit = DEFAULT_SELECTION_LIST_LIMIT,
        offset,
        search,
        excludeIds,
        includeIds,
        ordering,
      }) => {
        let queryString = `programdetectortriggers/?limit=${limit}`
        if (offset !== undefined) {
          queryString += `&offset=${offset}`
        }
        if (equipmentId) {
          queryString += `&signal__machine_sub_system=${equipmentId}`
        }
        if (search) {
          queryString += `&search=${encodeURIComponent(search)}`
        }
        if (excludeIds && excludeIds.length > 0) {
          queryString += `&exclude_ids=${excludeIds.join(',')}`
        }
        if (includeIds && includeIds.length > 0) {
          queryString += `&include_ids=${includeIds.join(',')}`
        }
        if (ordering) {
          queryString += `&ordering=${ordering}`
        }
        return {url: queryString}
      },
      providesTags: ['Trigger'],
    }),
    getObjectsDestroyedProgram: builder.query<
      BasicAffectedObjectsInterface,
      {
        programId: string
      }
    >({query: ({programId}) => `programs/${programId}/affected_objects_by_destroy/`}),
    getObjectsDestroyedEquipment: builder.query<
      BasicAffectedObjectsInterface,
      {
        equipmentId: string
      }
    >({query: ({equipmentId}) => `machinesubsystems/${equipmentId}/affected_objects_by_destroy/`}),
    getObjectsAffectedByDestroyTrigger: builder.query<
      BasicAffectedObjectsInterface,
      {
        triggerId: string
      }
    >({
      query: ({triggerId}) => `programdetectortriggers/${triggerId}/affected_objects_by_destroy/`,
    }),
    getSubscriptionUsage: builder.query<SubscriptionUsageInterface, void>({
      query: () => ({
        url: 'subscriptions/usage/',
        method: 'GET',
      }),
    }),
    createQuoteRequestPublic: builder.mutation({
      query: (body) => ({
        url: 'quoterequests/create_public/',
        method: 'POST',
        body,
        headers: {Authorization: ''},
      }),
    }),
    createPurchaseOrderRequestPublic: builder.mutation({
      query: (body) => ({
        url: 'purchaseorderrequests/create_public/',
        method: 'POST',
        body,
        headers: {Authorization: ''},
      }),
    }),
    generateReport: builder.mutation({
      query: (body) => ({
        url: 'reports/generate_report/',
        method: 'POST',
        body,
      }),
    }),
    updateDashboard: builder.mutation<
      {message: string; result: DashboardInterface},
      {
        dashboardId: string
        method: string
        body: {name?: string; description?: string} | FormData
      }
    >({
      query: ({dashboardId, method, body}) => ({
        url: `dashboards/${dashboardId}/`,
        method,
        body,
      }),
    }),
    updateSignalRollup: builder.mutation<
      {message: string; result: SignalRollupInterface},
      {
        signalRollupId: string
        method: string
        body: SignalRollupPayloadInterface | FormData
      }
    >({
      query: ({signalRollupId, method, body}) => ({
        url: `signalrollups/${signalRollupId}`,
        method,
        body,
      }),
    }),
    getRollupFormulaTerms: builder.query<RollupAggregatorTermInterface[], {rollupId: string}>({
      query: ({rollupId}) => `signalrollups/${rollupId}/formula-terms/`,
      providesTags: (_result, _error, {rollupId}) => [
        {type: 'SignalRollup' as const, id: `formula-${rollupId}`},
      ],
    }),
    addRollupFormulaTerm: builder.mutation<
      RollupAggregatorTermInterface,
      {rollupId: string; body: RollupAggregatorTermPayload}
    >({
      query: ({rollupId, body}) => ({
        url: `signalrollups/${rollupId}/formula-terms/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, {rollupId}) => [
        {type: 'SignalRollup' as const, id: `formula-${rollupId}`},
        'SignalRollup',
      ],
    }),
    deleteRollupFormulaTerm: builder.mutation<void, {rollupId: string; termId: string}>({
      query: ({rollupId, termId}) => ({
        url: `signalrollups/${rollupId}/formula-terms/`,
        method: 'DELETE',
        body: {term_id: termId},
      }),
      invalidatesTags: (_result, _error, {rollupId}) => [
        {type: 'SignalRollup' as const, id: `formula-${rollupId}`},
        'SignalRollup',
      ],
    }),
    /**
     * Create a rollup whose value is a formula over other rollups (AB#6160).
     *
     * Its own endpoint rather than the plain create, because the formula and the
     * rollup are written in one transaction — a derived rollup never exists
     * without its terms.
     */
    createDerivedRollup: builder.mutation<
      SignalRollupInterface,
      {body: DerivedSignalRollupPayloadInterface}
    >({
      query: ({body}) => ({
        url: 'signalrollups/create_derived_rollup/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SignalRollup'],
    }),
    createLine: builder.mutation<
      {url: string; machine_uuid?: string; message?: string},
      {
        lineId: string
        method?: string
        body: {
          name: string
          production_area: string
          machine_type: string
          // Optional: add the new line to a Resource Group on create (backend
          // assigns the membership + cascades).
          assign_to_resource_group?: string
        }
      }
    >({
      query: ({lineId, method, body}) => ({
        url: `machines/${lineId}`,
        method,
        body,
      }),
      invalidatesTags: ['Line'],
    }),
    // updateLine: builder.mutation<{ message: string, result: LineInterface }, {
    // 	id: string,
    // 	data: Partial<LineInterface> | FormData,
    // }>({
    // 	query: ({id, data}) => {
    // 		const isFormData = data instanceof FormData
    // 		return {
    // 		url: `machines/${id}/`,
    // 		method: 'PATCH',
    // 		body: data,
    // 		headers: isFormData ? {} : {'Content-Type': 'application/json'},
    // 	}
    // 	},
    // }),
    updateLine: builder.mutation<
      {message: string; result: LineInterface},
      {id: string; data: Partial<LineInterface> | FormData}
    >({
      query: ({id, data}) => {
        let body: Partial<LineInterface> | FormData
        let headers: Record<string, string> | undefined

        if (data instanceof FormData) {
          // FormData for image upload
          body = data
          headers = undefined // Browser sets correct Content-Type
        } else {
          // JSON for other updates
          body = data
          headers = {'Content-Type': 'application/json'}
        }

        return {
          url: `machines/${id}/`,
          method: 'PATCH',
          body,
          headers,
        }
      },
      invalidatesTags: ['Line'],
    }),

    updateNotificationRead: builder.mutation<
      {message: string},
      {
        notificationId: string
        readByCurrentUser: boolean
      }
    >({
      query: ({notificationId, readByCurrentUser}) => ({
        url: `notifications/${notificationId}/read_status/`,
        method: 'POST',
        body: {read_by_current_user: readByCurrentUser},
      }),
    }),
    updateNotificationAllRead: builder.mutation<{message: string}, {readByCurrentUser: boolean}>({
      query: ({readByCurrentUser}) => ({
        url: 'notifications/read_status_all/',
        method: 'POST',
        body: {read_by_current_user: readByCurrentUser},
      }),
    }),
    getNotifications: builder.mutation<NotificationInterface[], void>({
      query: () => 'notifications/',
      transformResponse: (response: BaseFetchResultsInterface<NotificationInterface>) =>
        response.results,
    }),
    getLatestRunningProgram: builder.query<ProgramInterface, {equipmentId: string}>({
      query: ({equipmentId}) => `machinesubsystems/${equipmentId}/latest_running_program/`,
      transformResponse: (response: BaseFetchResultInterface<ProgramInterface>) => response.result,
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getLatestRunningBatch: builder.query<BatchInterface, {equipmentId: string}>({
      query: ({equipmentId}) => ({
        url: `machinesubsystems/${equipmentId}/last_running_batch/`,
        method: 'GET',
      }),
      transformResponse: (response: BaseFetchResultInterface<BatchInterface>) => response.result,
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getSignalValueTrackers: builder.query<
      BaseFetchResultsInterface<SignalValueTrackerInterface>,
      {
        limit: number
        offset: number
        search?: string
        ordering?: string
        startDate?: string
        endDate?: string
        signalId?: string
        filters?: {
          productionAreaId?: string
          lineId?: string
          equipmentId?: string
        }
      }
    >({
      query: ({limit, offset, search, ordering, startDate, endDate, signalId, filters}) => ({
        url: 'signal-value-trackers/',
        params: {
          limit: limit,
          offset: offset,
          search: search || undefined,
          ordering: ordering,
          t_start__gte: startDate || undefined,
          t_start__lte: endDate || undefined,
          signal: signalId || undefined,
          signal__machine__production_area: filters?.productionAreaId || undefined,
          signal__machine: filters?.lineId || undefined,
          signal__machine_sub_system: filters?.equipmentId || undefined,
        },
      }),
    }),
    getSignalValueTrackerTopValues: builder.query<
      SignalValueTrackerTopValuesResponseInterface,
      {
        signalId?: string
        tStart?: string
        tEnd?: string
        limit?: number
        excludeValues?: string[]
      }
    >({
      query: ({signalId, tStart, tEnd, limit = 5, excludeValues}) => ({
        url: 'signal-value-trackers/top-values/',
        params: {
          signal: signalId,
          t_start: tStart,
          t_end: tEnd,
          limit,
          exclude_values: excludeValues?.length ? excludeValues.join(',') : undefined,
        },
      }),
    }),
    upsertSmartFilter: builder.mutation<
      BaseFetchResultsInterface<SmartFilterInterface>,
      {
        preferred_production_areas?: string[]
        preferred_machines?: string[]
        preferred_machine_subsystems?: string[]
      }
    >({
      query: (body) => ({
        url: 'smart-filters/', // adjust if your DRF router uses a different path
        method: 'POST',
        body,
      }),
    }),
    exportSignalValueTrackersExcel: builder.mutation<
      Blob,
      {
        search?: string
        startDate?: string
        endDate?: string
        signalId?: string
        filters?: {
          productionAreaId?: string
          lineId?: string
          equipmentId?: string
        }
      }
    >({
      query: ({search, startDate, endDate, signalId, filters}) => ({
        url: 'signal-value-trackers/export-excel/',
        method: 'GET',
        headers: {Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'},
        params: {
          search: search || undefined,
          t_start__gte: startDate || undefined,
          t_start__lte: endDate || undefined,
          signal: signalId || undefined,
          signal__machine__production_area: filters?.productionAreaId || undefined,
          signal__machine: filters?.lineId || undefined,
          signal__machine_sub_system: filters?.equipmentId || undefined,
        },
        responseHandler: (response: Response): Promise<Blob> => response.blob(),
      }),
    }),
    /**
     * Test a notification subscription through core (AB#6224): dry-run
     * resolution preview (send=false, default) or an actual "[Test]" send to
     * the previewed recipients (send=true). Core resolves the subscription's
     * scope against the tenant's resource groups and hands the gateway the
     * access set a real alert would carry, so the preview shows who really
     * gets the alert -- and names the scoped records and their groups.
     * Deliberately invalidates nothing: a test must not refetch any list.
     */
    testNotificationSubscription: builder.mutation<
      SubscriptionTestResponseInterface,
      {id: string; send?: boolean}
    >({
      query: ({id, send = false}) => ({
        url: `notification-subscriptions/${id}/test/`,
        method: 'POST',
        body: {send},
      }),
    }),
  }),
})

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints

// Queries
export const {
  useGetAllLogbookEntriesQuery,
  useGetObjectsAffectedByDestroyTriggerQuery,
  useGetObjectsDestroyedEquipmentQuery,
  useGetObjectsDestroyedProgramQuery,
  useGetSignalRollupsQuery,
  useGetSignalRollupsByIdsQuery,
  useGetSignalRollupsPaginatedQuery,
  useLazyGetSignalRollupsPaginatedQuery,
  useGetLineQuery,
  useGetNotificationsMutation,
  useGetLinesQuery,
  useGetLinesWithSearchQuery,
  useGetEquipmentsQuery,
  useLazyGetEquipmentsWithCountQuery,
  useGetEquipmentsWithSearchQuery,
  useGetEquipmentsWithSearchPaginatedQuery,
  useLazyGetEquipmentsWithSearchPaginatedQuery,
  useGetExternalBookmarksQuery,
  useGetLinesWithSubSystemsQuery,
  useGetProgramDetectorTriggersQuery,
  useGetBuildingsQuery,
  useGetLineTypesPaginatedQuery,
  useLazyGetSignalsObjectsAffectedDestroyQuery,
  useGetEquipmentTraceQuery,
  useGetDashboardsQuery,
  useGetDashboardsByIdQuery,
  useGetSignalRollupsWithValueQuery,
  useLazyGetSignalRollupsWithValueQuery,
  useGetSignalRollupsWithValuePaginatedQuery,
  useGetThresholdsWithValueQuery,
  useGetLinesWithNumberOfSignalsQuery,
  useGetSubscriptionsQuery,
  useGetLatestRunningProgramQuery,
  useGetLatestRunningBatchQuery,
  useGetSubscriptionUsageQuery,
  useLazyGetEquipmentsQuery,
  useGetEquipmentsWithCountQuery,
  useGetLinesWithCountQuery,
  useLazyGetLinesWithCountQuery,
  useGetSignalValueTrackersQuery,
  useLazyGetSignalValueTrackersQuery,
  useGetSignalValueTrackerTopValuesQuery,
  useGetSmartFilterQuery,
} = baseApi

// Mutations
export const {
  useCreateLineMutation,
  useUpdateNotificationReadMutation,
  useUpdateNotificationAllReadMutation,
  useUpdateSignalRollupMutation,
  useCreateDerivedRollupMutation,
  useGetRollupFormulaTermsQuery,
  useAddRollupFormulaTermMutation,
  useDeleteRollupFormulaTermMutation,
  useUpdateDashboardMutation,
  useGenerateReportMutation,
  useUpdateLineMutation,
  useExportSignalValueTrackersExcelMutation,
  useUpsertSmartFilterMutation,
  useTestNotificationSubscriptionMutation,
} = baseApi
